import React, {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {BaseInputProps} from '../..';

import {Tooltip} from '@mantine/core';
import {ErrorOption, useController, useFormContext} from 'react-hook-form';
import {RiInformationFill} from 'react-icons/ri';
import {NestedKeys} from 'shared/types/key-manipulation';

import {Container, ContainerLabel, Label, Input as StyledInput} from './styles';
import ErrorWrapper from '../error-wrapper';

export type PriceInputProps<T = any> = BaseInputProps<T> & {
  name: NestedKeys<T>;
  gridColumn?: number;
};

const PriceInput: React.FC<PriceInputProps> = ({
  name,
  label,
  gridColumn,
  border,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hidden,
  information,
  required,
  ...props
}) => {
  const {control, formState, clearErrors} = useFormContext();
  const {field} = useController({control, name: name!});
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState<string>();

  const error: ErrorOption | undefined = formState.errors
    ? formState.errors[name as string]
    : undefined;

  useEffect(() => {
    if (field.value && !value) initializeDefaultValue();
  }, [value, field.value]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearErrors(name);
    const value = event.target.value;
    const numericValue = value.replace(/[^\d]/g, '');

    setValue(numericValue);

    const integer = numericValue.slice(0, -2) || '0';
    const decimal = numericValue.slice(-2);

    field.onChange(+String(`${+integer}.${decimal}`));
  };

  const initializeDefaultValue = () => {
    const [integer, decimal] = String(field.value).split('.');

    if (!decimal) return setValue(String(field.value) + '00');

    if (decimal.length === 1) return setValue(String(field.value) + '0');

    return setValue(`${integer}${decimal}`);
  };

  const getValue = useCallback((value?: string) => {
    if (!value) return '';

    const numericValue = parseFloat(value.replace(/[^\d]/g, '')) / 100;

    if (isNaN(numericValue)) {
      return 'R$ 0,00';
    }

    return numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }, []);

  return (
    <ErrorWrapper
      {...{
        error: error?.message,
        gridColumn,
      }}>
      <Container {...{border, focused, error}}>
        <ContainerLabel>
          {label && (
            <Label {...{error: !!error}}>
              {label}
              {required && ' *'}
            </Label>
          )}
          {information && (
            <Tooltip
              label={information}
              multiline
              withArrow
              arrowSize={10}
              openDelay={500}
              w={200}
              position="top">
              <div>
                <RiInformationFill size={20} />
              </div>
            </Tooltip>
          )}
        </ContainerLabel>
        <StyledInput
          {...{
            ...props,
            ...field,
            value: getValue(value),
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
            onChange,
            error: !!error,
          }}
        />
      </Container>
    </ErrorWrapper>
  );
};

export default PriceInput;
